import { isAsyncFunction, isSyncFunction } from '~theme/utils/asyncFunction';
import { watchRecord } from '~theme/utils/watchRecord';

export type Validatable = Nullish<
  | string
  | number
  | boolean
  | (string | number)[]
  | Record<string, string | number | boolean>
>;
const isValidatable = (value: unknown): value is Validatable => {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    (Array.isArray(value) &&
      value.every(
        (item) => typeof item === 'string' || typeof item === 'number'
      )) ||
    value === null ||
    (typeof value === 'object' &&
      Object.values(value).every(
        (item) =>
          typeof item === 'string' ||
          typeof item === 'number' ||
          typeof item === 'boolean'
      )) ||
    value === undefined
  );
};
export type ValidationRule<T extends Validatable> =
  | {
      errorMessage: string;
      validate: (value: T) => MaybePromise<boolean>;
    }
  | {
      errorMessage: string;
      required: true;
    };
export type ValidationRules<T extends Validatable> = ValidationRule<T>[];

// フィールドがValidatableじゃないキーはバリデーション対象から外す
type ValidationNeededStateKey<State extends Record<string, any>> = {
  [K in keyof State]: State[K] extends Validatable ? K : never;
}[keyof State];
type ValidationRulesRecords<State extends Record<string, any>> = {
  [K in ValidationNeededStateKey<State>]: ValidationRules<State[K]>;
};

export type ValidationError = {
  errorMessage: string;
};
export type ValidatedResult<State extends Record<string, any>> = Record<
  ValidationNeededStateKey<State>,
  Maybe<ValidationError>
>;

const VALIDATING: unique symbol = Symbol('VALIDATING');

export function useValidation<State extends Record<string, any>>(
  state: RefOrGetterFunction<State>,
  rules: MaybeRefOrGetter<ValidationRulesRecords<State>>
) {
  // lazyErrorのために、ステート変更が入ったかどうかを持つ
  const hasStateChangedByFields = ref(
    Object.fromEntries(Object.keys(toValue(state)).map((key) => [key, false]))
  );
  // 非同期バリデーション中はVALIDATING、バリデーション終了後にValidationErrorを返したい。
  // asyncComputedで管理すると、バリデーション中という状態がない。
  // 初回入力前はバリデーションエラーが出るが、バリデーションが完了するとエラーが消えるパターンの場合、
  // 初回入力～入力のバリデーション完了までの一瞬でバリデーションエラーが出てしまう。
  // そのため、asyncComputedではなくrefで管理する
  const errorFieldsState = ref<{
    [K: string]: Maybe<ValidationError> | typeof VALIDATING;
  }>({
    ...Object.fromEntries(
      Object.keys(toValue(state)).map((key) => [key, VALIDATING])
    ),
  });
  validateAllKeys();

  watchRecord(state, (newState, _, changedStateKeys) => {
    changedStateKeys.forEach((changedStateKey) => {
      hasStateChangedByFields.value[changedStateKey as string] = true;

      const value = newState[changedStateKey];
      const rulesForAKey =
        toValue(rules)[changedStateKey as ValidationNeededStateKey<State>];
      if (!isValidatable(value)) {
        // Validatableじゃなかったら無視する
        return;
      }
      validateSingleValue(
        changedStateKey as string,
        value,
        rulesForAKey as ValidationRules<Validatable>
      );
    });
  });

  if (isRef(rules) || typeof rules === 'function') {
    watchRecord(rules, (newRules, _, changedRuleKeys) => {
      changedRuleKeys.forEach((changedRuleKey) => {
        const value = toValue(state)[changedRuleKey];
        if (!isValidatable(value)) {
          // Validatableじゃなかったら無視する
          return;
        }
        validateSingleValue(
          changedRuleKey as string,
          value,
          newRules[changedRuleKey] as ValidationRules<Validatable>
        );
      });
    });
  }

  const resetLazyValidation = () => {
    hasStateChangedByFields.value = Object.fromEntries(
      Object.keys(toValue(state)).map((key) => [key, false])
    );
  };

  async function validateSingleValue(
    key: string,
    value: Validatable,
    rules: ValidationRules<Validatable>
  ) {
    for await (const rule of rules) {
      if ('required' in rule) {
        if (value == null) {
          errorFieldsState.value[key] = {
            errorMessage: rule.errorMessage,
          };
          return;
        }
        continue;
      }

      if (isSyncFunction(rule.validate)) {
        const isValid = rule.validate(value);
        if (!isValid) {
          errorFieldsState.value[key] = {
            errorMessage: rule.errorMessage,
          };
          return;
        }
      } else if (isAsyncFunction(rule.validate)) {
        errorFieldsState.value[key] = VALIDATING;
        const isValid = await rule.validate(value);
        if (!isValid) {
          errorFieldsState.value[key] = {
            errorMessage: rule.errorMessage,
          };
          return;
        }
      }
    }
    errorFieldsState.value[key] = undefined;
  }
  function validateAllKeys() {
    const stateValue = toValue(state);
    const rulesValue = toValue(rules);

    Object.entries(rulesValue).forEach(([key, rules]) => {
      if (!(key in stateValue)) {
        // stateにkeyがなかったら無視する
        return;
      }
      const value = stateValue[key];
      if (!isValidatable(value)) {
        // Validatableじゃなかったら無視する
        return;
      }
      validateSingleValue(key, value, rules as ValidationRules<Validatable>);
    });
  }

  const errorFields = computed(() => {
    return Object.fromEntries(
      Object.entries(errorFieldsState.value).map(([key, errorOrValidating]) => {
        if (errorOrValidating === VALIDATING) {
          return [
            key,
            {
              errorMessage: '入力内容のチェックが完了していません',
            },
          ] as const;
        }
        return [key, errorOrValidating] as const;
      })
    );
  });
  const typeAssertedErrorFields = errorFields as ComputedRef<
    ValidatedResult<State>
  >;
  const errors = computed(() => {
    return Object.values(errorFields.value).filter(
      (errorField): errorField is ValidationError => errorField != undefined
    );
  });

  const lazyErrorFields = computed(() => {
    return Object.fromEntries(
      Object.entries(errorFieldsState.value).map(([key, errorOrValidating]) => {
        if (
          errorOrValidating === VALIDATING ||
          !hasStateChangedByFields.value[key]
        ) {
          return [key, undefined] as const;
        }
        return [key, errorOrValidating] as const;
      })
    );
  });
  const typeAssertedLazyErrorFields = lazyErrorFields as ComputedRef<
    ValidatedResult<State>
  >;
  const lazyErrors = computed(() => {
    return Object.values(lazyErrorFields.value).filter(
      (errorField): errorField is ValidationError => errorField != undefined
    );
  });

  const isValid = computed(() => {
    return errors.value.length === 0;
  });

  return {
    // ステート変更するしないに関わらずバリデーションを実行する
    isValid,
    errors,
    errorFields: typeAssertedErrorFields,

    // 初回のステート変更までバリデーションを実行しない
    lazyErrors,
    lazyErrorFields: typeAssertedLazyErrorFields,
    // 初回のステート変更の状態をリセットする
    resetLazyValidation,
  };
}
